import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { tokenStorage } from '../utils/tokenStorage';
import {
  webSocketService,
  ConnectionState,
  WebSocketEvent,
  TeleChatMessagePayload,
  TeleChatListPayload,
} from '../services/websocket.service';

interface WebSocketContextType {
  connectionState: ConnectionState;
  isConnected: boolean;
  subscribeToChatMessages: (
    chatId: number,
    callback: (event: WebSocketEvent<TeleChatMessagePayload>) => void
  ) => () => void;
  subscribeToLobbyUpdates: (
    callback: (event: WebSocketEvent<TeleChatListPayload>) => void
  ) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [connectionState, setConnectionState] = useState<ConnectionState>('DISCONNECTED');

  // Connect/disconnect based on authentication state
  useEffect(() => {
    if (isAuthenticated) {
      const token = tokenStorage.getAccessToken();
      if (token) {
        webSocketService.connect(token);
      }
    } else {
      webSocketService.disconnect();
    }

    return () => {
      // Don't disconnect on unmount if still authenticated
      // This prevents disconnect during React StrictMode double-mount
    };
  }, [isAuthenticated]);

  // Listen for connection state changes
  useEffect(() => {
    const unsubscribe = webSocketService.onConnectionStateChange((state) => {
      setConnectionState(state);
    });

    return unsubscribe;
  }, []);

  // Disconnect on logout event
  useEffect(() => {
    const handleLogout = () => {
      webSocketService.disconnect();
    };

    window.addEventListener('auth:unauthorized', handleLogout);
    return () => {
      window.removeEventListener('auth:unauthorized', handleLogout);
    };
  }, []);

  // Reconnect when app returns from background (phone unlock, tab focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When page becomes visible and user is authenticated, reconnect WebSocket
      // iOS Safari doesn't fire this event when locking phone, only when unlocking
      // So we simply reconnect whenever page becomes visible to ensure fresh connection
      if (!document.hidden && isAuthenticated) {
        const token = tokenStorage.getAccessToken();
        if (token) {
          webSocketService.reconnect();
        }
      }
    };

    const handleOnline = () => {
      if (isAuthenticated) {
        const token = tokenStorage.getAccessToken();
        if (token) {
          webSocketService.reconnect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [isAuthenticated]);

  const subscribeToChatMessages = useCallback(
    (chatId: number, callback: (event: WebSocketEvent<TeleChatMessagePayload>) => void) => {
      return webSocketService.subscribeToChatMessages(chatId, callback);
    },
    []
  );

  const subscribeToLobbyUpdates = useCallback(
    (callback: (event: WebSocketEvent<TeleChatListPayload>) => void) => {
      return webSocketService.subscribeToLobbyUpdates(callback);
    },
    []
  );

  const value: WebSocketContextType = {
    connectionState,
    isConnected: connectionState === 'CONNECTED',
    subscribeToChatMessages,
    subscribeToLobbyUpdates,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
